import React, { useState, useEffect, useMemo } from 'react';
import chemicalService from '../../../services/chemicalService';

const UNIT_OPTIONS = ['Kg', 'Liter', 'Gram', 'ML', 'Piece', 'Meter'];
const LOCATION_OPTIONS = ['', 'Warehouse A', 'Warehouse B', 'Warehouse C', 'Warehouse D'];
const PAYMENT_MODES = ['cash', 'bank_transfer', 'cheque', 'easypaisa', 'jazzcash'];

const blankItem = () => ({
  product_id: '',
  batch_no: '',
  mfg_date: new Date().toISOString().split('T')[0],
  expiry_date: '',
  quantity: '',
  unit: 'Kg',
  rate: '',
  amount: '',
  location: '',
});

const PurchaseFormModal = ({ onClose, onSaved }) => {
  // ─── Master data ──
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingMaster, setLoadingMaster] = useState(true);

  // ─── Form state ──
  const [form, setForm] = useState({
    bill_no: '',
    vendor_id: '',
    purchase_date: new Date().toISOString().split('T')[0],
    gst_percent: 17,
    discount: 0,
    amount_paid: 0,
    payment_mode: 'cash',
    notes: '',
    items: [blankItem()],
  });

  const [submitting, setSubmitting] = useState(false);

  // Load vendors + products
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingMaster(true);
        const [vendorsRes, productsRes] = await Promise.all([
          chemicalService.getVendors({ page: 1, limit: 1000 }),
          chemicalService.getProducts({ page: 1, limit: 1000 }),
        ]);
        setVendors((vendorsRes.data || []).filter((v) => v.is_active));
        setProducts((productsRes.data || []).filter((p) => p.is_active));
      } catch (err) {
        console.error('Error loading vendors/products:', err);
        alert('❌ Failed to load vendors or products');
      } finally {
        setLoadingMaster(false);
      }
    };
    load();
  }, []);

  // ─── Computed totals ──
  const { subtotal, gstAmount, grandTotal } = useMemo(() => {
    const sub = form.items.reduce((sum, item) => {
      const amt = parseFloat(item.amount);
      return sum + (isNaN(amt) ? 0 : amt);
    }, 0);
    const gstPct = parseFloat(form.gst_percent) || 0;
    const disc = parseFloat(form.discount) || 0;
    const afterDiscount = sub - disc;
    const gst = afterDiscount * (gstPct / 100);
    const grand = afterDiscount + gst;
    return {
      subtotal: sub,
      gstAmount: gst,
      grandTotal: grand,
    };
  }, [form.items, form.gst_percent, form.discount]);

  // Auto-clamp amount_paid to grand_total
  useEffect(() => {
    const paid = parseFloat(form.amount_paid) || 0;
    if (paid > grandTotal && grandTotal > 0) {
      setForm((prev) => ({ ...prev, amount_paid: grandTotal.toFixed(2) }));
    }
  }, [grandTotal, form.amount_paid]);

  // ─── Payment status ──
  const paymentStatus = useMemo(() => {
    const paid = parseFloat(form.amount_paid) || 0;
    if (paid <= 0) return 'unpaid';
    if (paid >= grandTotal) return 'paid';
    return 'partial';
  }, [form.amount_paid, grandTotal]);

  // ─── Handlers ──
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };

      // Auto-fill unit from product when product changes
      if (field === 'product_id' && value) {
        const product = products.find((p) => p.id === parseInt(value, 10));
        if (product?.base_unit) {
          items[index].unit = product.base_unit;
        }
      }

      // Auto-compute amount when qty or rate changes (only if user hasn't manually edited it)
      if (field === 'quantity' || field === 'rate') {
        const q = parseFloat(field === 'quantity' ? value : items[index].quantity);
        const r = parseFloat(field === 'rate' ? value : items[index].rate);
        if (!isNaN(q) && !isNaN(r) && q > 0 && r >= 0) {
          items[index].amount = (q * r).toFixed(2).replace(/\.?0+$/, '');
        }
      }

      return { ...prev, items };
    });
  };

  const addItem = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, blankItem()] }));
  };

  const removeItem = (index) => {
    if (form.items.length === 1) {
      alert('❌ A purchase needs at least one item.');
      return;
    }
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Validate header
    if (!form.bill_no.trim()) return alert('❌ Bill number is required.');
    if (!form.vendor_id) return alert('❌ Select a vendor.');
    if (!form.purchase_date) return alert('❌ Purchase date is required.');

    // Validate items
    const seenBatchNos = new Set();
    for (let i = 0; i < form.items.length; i++) {
      const item = form.items[i];
      const idx = i + 1;
      if (!item.product_id) return alert(`❌ Item ${idx}: select a product.`);
      if (!item.batch_no.trim()) return alert(`❌ Item ${idx}: batch number is required.`);
      if (!item.mfg_date) return alert(`❌ Item ${idx}: mfg date is required.`);
      if (!item.quantity || parseFloat(item.quantity) <= 0) return alert(`❌ Item ${idx}: quantity must be > 0.`);
      if (item.rate === '' || parseFloat(item.rate) < 0) return alert(`❌ Item ${idx}: rate must be ≥ 0.`);
      if (!item.amount) return alert(`❌ Item ${idx}: amount is required.`);
      if (item.expiry_date && item.expiry_date < item.mfg_date) {
        return alert(`❌ Item ${idx}: expiry cannot be before mfg date.`);
      }
      if (seenBatchNos.has(item.batch_no.trim())) {
        return alert(`❌ Item ${idx}: duplicate batch number "${item.batch_no}".`);
      }
      seenBatchNos.add(item.batch_no.trim());
    }

    if (grandTotal <= 0) {
      return alert('❌ Grand total must be > 0.');
    }
    if (parseFloat(form.amount_paid) > grandTotal) {
      return alert('❌ Amount paid cannot exceed grand total.');
    }

    try {
      setSubmitting(true);
      const payload = {
        bill_no: form.bill_no.trim(),
        vendor_id: parseInt(form.vendor_id, 10),
        purchase_date: form.purchase_date,
        subtotal: parseFloat(subtotal.toFixed(2)),
        gst_percent: parseFloat(form.gst_percent) || 0,
        gst_amount: parseFloat(gstAmount.toFixed(2)),
        discount: parseFloat(form.discount) || 0,
        grand_total: parseFloat(grandTotal.toFixed(2)),
        amount_paid: parseFloat(form.amount_paid) || 0,
        payment_mode: parseFloat(form.amount_paid) > 0 ? form.payment_mode : null,
        notes: form.notes.trim() || null,
        items: form.items.map((item) => ({
          product_id: parseInt(item.product_id, 10),
          batch_no: item.batch_no.trim(),
          mfg_date: item.mfg_date,
          expiry_date: item.expiry_date || null,
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          rate: parseFloat(item.rate),
          amount: parseFloat(item.amount),
          location: item.location || null,
        })),
      };

      await chemicalService.createPurchase(payload);
      alert('✅ Purchase recorded! Inventory updated, vendor balance adjusted.');
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create purchase';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>🛒 New Purchase</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              Record a vendor bill — new batches will be created in inventory
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* ─── Bill Header ──────────────────── */}
            <div style={styles.sectionLabel}>Bill Information</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Bill No. <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={form.bill_no}
                  onChange={(e) => handleChange('bill_no', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. BILL-0238"
                  required
                />
              </div>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>
                  Vendor <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.vendor_id}
                  onChange={(e) => handleChange('vendor_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingMaster}
                >
                  <option value="">
                    {loadingMaster
                      ? 'Loading vendors...'
                      : vendors.length === 0
                        ? '-- No active vendors --'
                        : '-- Select vendor --'}
                  </option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}{v.ntn ? ` (NTN ${v.ntn})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Purchase Date <span style={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={form.purchase_date}
                  onChange={(e) => handleChange('purchase_date', e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            {/* ─── Items ─────────────────────────── */}
            <div style={styles.sectionLabel}>
              Items ({form.items.length})
              <span style={styles.deductHint}>(each item creates a new batch)</span>
            </div>

            <div style={styles.itemsList}>
              {form.items.map((item, index) => {
                return (
                  <div key={index} style={styles.itemCard}>
                    <div style={styles.itemHeader}>
                      <div style={styles.itemIndex}>{index + 1}</div>
                      <span style={styles.itemTitle}>Item {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        style={styles.removeButton}
                        title="Remove"
                        disabled={form.items.length === 1}
                      >
                        🗑️
                      </button>
                    </div>

                    {/* Row 1: product + batch + location */}
                    <div style={styles.itemRow}>
                      <div style={{ flex: 3, minWidth: '200px' }}>
                        <label style={styles.miniLabel}>Product *</label>
                        <select
                          value={item.product_id}
                          onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                          style={styles.input}
                          required
                          disabled={loadingMaster}
                        >
                          <option value="">-- Select --</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.code} — {p.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: 2, minWidth: '160px' }}>
                        <label style={styles.miniLabel}>Batch No *</label>
                        <input
                          type="text"
                          value={item.batch_no}
                          onChange={(e) => handleItemChange(index, 'batch_no', e.target.value)}
                          style={styles.input}
                          placeholder="e.g. CHM-2026-101"
                          required
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '130px' }}>
                        <label style={styles.miniLabel}>Location</label>
                        <select
                          value={item.location}
                          onChange={(e) => handleItemChange(index, 'location', e.target.value)}
                          style={styles.input}
                        >
                          {LOCATION_OPTIONS.map((loc) => (
                            <option key={loc} value={loc}>{loc || 'None'}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Row 2: mfg/expiry + qty/unit + rate/amount */}
                    <div style={styles.itemRow}>
                      <div style={{ flex: 1, minWidth: '120px' }}>
                        <label style={styles.miniLabel}>Mfg Date *</label>
                        <input
                          type="date"
                          value={item.mfg_date}
                          onChange={(e) => handleItemChange(index, 'mfg_date', e.target.value)}
                          style={styles.input}
                          required
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '120px' }}>
                        <label style={styles.miniLabel}>Expiry Date</label>
                        <input
                          type="date"
                          value={item.expiry_date}
                          onChange={(e) => handleItemChange(index, 'expiry_date', e.target.value)}
                          style={styles.input}
                          min={item.mfg_date || undefined}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '90px' }}>
                        <label style={styles.miniLabel}>Qty *</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          style={styles.input}
                          placeholder="0"
                          required
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '90px' }}>
                        <label style={styles.miniLabel}>Unit *</label>
                        <select
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          style={styles.input}
                          required
                        >
                          {UNIT_OPTIONS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: 1, minWidth: '110px' }}>
                        <label style={styles.miniLabel}>Rate (Rs) *</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                          style={styles.input}
                          placeholder="0"
                          required
                        />
                      </div>
                      <div style={{ flex: 1.3, minWidth: '120px' }}>
                        <label style={styles.miniLabel}>Amount (Rs) *</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.amount}
                          onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                          style={{ ...styles.input, fontWeight: '700', color: '#27ae60' }}
                          placeholder="0"
                          required
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button type="button" onClick={addItem} style={styles.addItemButton}>
              + Add item
            </button>

            {/* ─── Totals ────────────────────────── */}
            <div style={styles.sectionLabel}>Totals</div>
            <div style={styles.totalsCard}>
              <div style={styles.totalsRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>GST %</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={form.gst_percent}
                    onChange={(e) => handleChange('gst_percent', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Discount (Rs)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.discount}
                    onChange={(e) => handleChange('discount', e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.totalsBreakdown}>
                <div style={styles.totalLine}>
                  <span>Subtotal</span>
                  <strong>Rs {formatNumber(subtotal)}</strong>
                </div>
                {parseFloat(form.discount) > 0 && (
                  <div style={styles.totalLine}>
                    <span>Discount</span>
                    <strong style={{ color: '#e74c3c' }}>− Rs {formatNumber(form.discount)}</strong>
                  </div>
                )}
                <div style={styles.totalLine}>
                  <span>GST ({form.gst_percent}%)</span>
                  <strong>Rs {formatNumber(gstAmount)}</strong>
                </div>
                <div style={{ ...styles.totalLine, ...styles.grandTotal }}>
                  <span>Grand Total</span>
                  <strong>Rs {formatNumber(grandTotal)}</strong>
                </div>
              </div>
            </div>

            {/* ─── Payment ───────────────────────── */}
            <div style={styles.sectionLabel}>Payment</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount Paid (Rs)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={grandTotal}
                  value={form.amount_paid}
                  onChange={(e) => handleChange('amount_paid', e.target.value)}
                  style={styles.input}
                  placeholder="0"
                />
                <span style={styles.hint}>Leave at 0 if unpaid. Max Rs {formatNumber(grandTotal)}</span>
              </div>
              {parseFloat(form.amount_paid) > 0 && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Mode</label>
                  <select
                    value={form.payment_mode}
                    onChange={(e) => handleChange('payment_mode', e.target.value)}
                    style={styles.input}
                  >
                    {PAYMENT_MODES.map((m) => (
                      <option key={m} value={m}>{formatPaymentMode(m)}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <div style={{ marginTop: '6px' }}>
                  <span style={getStatusBadge(paymentStatus)}>
                    {paymentStatus === 'paid' ? '✓ Paid' : paymentStatus === 'partial' ? 'Partial' : 'Unpaid'}
                  </span>
                  {paymentStatus === 'partial' && (
                    <div style={{ fontSize: '12px', color: '#856404', marginTop: '4px' }}>
                      Rs {formatNumber(grandTotal - parseFloat(form.amount_paid || 0))} remaining
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div style={{ ...styles.formGroup, marginTop: '8px' }}>
              <label style={styles.label}>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                style={{ ...styles.input, minHeight: '50px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="Optional notes about this purchase..."
                rows="2"
              />
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : '💾 Save Purchase'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatPaymentMode = (m) => {
  const map = {
    cash: 'Cash',
    bank_transfer: 'Bank Transfer',
    cheque: 'Cheque',
    easypaisa: 'EasyPaisa',
    jazzcash: 'JazzCash',
  };
  return map[m] || m;
};

const getStatusBadge = (status) => {
  const palette = {
    paid:    { bg: '#d4edda', fg: '#155724' },
    partial: { bg: '#fff3cd', fg: '#856404' },
    unpaid:  { bg: '#f8d7da', fg: '#721c24' },
  };
  const c = palette[status] || palette.unpaid;
  return {
    background: c.bg, color: c.fg,
    padding: '6px 14px', borderRadius: '12px',
    fontSize: '13px', fontWeight: '700',
    display: 'inline-block',
  };
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '1100px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },
  sectionLabel: {
    fontSize: '12px', fontWeight: '700',
    color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px',
    marginTop: '20px', marginBottom: '12px',
    paddingBottom: '6px', borderBottom: '1px solid #f0f0f0',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  deductHint: {
    background: '#e3f2fd', color: '#1565c0',
    padding: '2px 10px', borderRadius: '10px',
    fontSize: '11px', fontWeight: '700',
    textTransform: 'none', letterSpacing: 0,
  },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: {
    flex: 1, minWidth: '180px',
    display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '12px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  miniLabel: {
    fontSize: '11px', fontWeight: '600',
    color: '#7f8c8d', marginBottom: '4px', display: 'block',
    textTransform: 'uppercase', letterSpacing: '0.3px',
  },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  hint: {
    fontSize: '11px', color: '#95a5a6', fontStyle: 'italic',
  },

  itemsList: {
    display: 'flex', flexDirection: 'column', gap: '12px',
    marginBottom: '12px',
  },
  itemCard: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '14px',
    border: '1px solid #e0e0e0',
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px dashed #e0e0e0',
  },
  itemIndex: {
    width: '24px', height: '24px',
    borderRadius: '50%',
    background: '#e74c3c',
    color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '700',
    flexShrink: 0,
  },
  itemTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#7f8c8d',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    flex: 1,
  },
  itemRow: {
    display: 'flex', gap: '10px', flexWrap: 'wrap',
    marginBottom: '8px',
  },
  removeButton: {
    padding: '6px 12px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  addItemButton: {
    width: '100%',
    padding: '12px',
    background: 'white',
    color: '#e74c3c',
    border: '2px dashed #e74c3c',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '700',
    marginBottom: '12px',
  },

  totalsCard: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '16px',
    border: '1px solid #e0e0e0',
  },
  totalsRow: {
    display: 'flex', gap: '16px', flexWrap: 'wrap',
    marginBottom: '12px',
    paddingBottom: '12px',
    borderBottom: '1px dashed #e0e0e0',
  },
  totalsBreakdown: {
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  totalLine: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    fontSize: '14px',
    color: '#2c3e50',
  },
  grandTotal: {
    marginTop: '6px',
    paddingTop: '12px',
    borderTop: '2px solid #2c3e50',
    fontSize: '18px',
    fontWeight: '700',
  },

  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    position: 'sticky', bottom: 0, background: 'white',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default PurchaseFormModal;